import { useEffect, useRef, useState } from 'react'
import {
  ArrowUpRight,
  FileLock2,
  Fingerprint,
  ShieldCheck,
  UsersRound,
  X,
} from 'lucide-react'
import {
  operatorApi,
  type OperatorEvidenceDto,
  type OperatorEventEvidenceDrawerDto,
  type OperatorProjectionEventDto,
  type OperatorTimelineDto,
} from '../../services/operatorApi'

interface EvidenceDrawerProps {
  event: OperatorProjectionEventDto
  timeline: OperatorTimelineDto
  onClose: () => void
}

function participant(
  timeline: OperatorTimelineDto,
  participantId: string | null,
) {
  if (!participantId) return null
  for (const party of timeline.hierarchy) {
    const person = [
      ...party.people,
      ...party.teams.flatMap((team) => team.people),
    ].find((candidate) => candidate.id === participantId)
    if (person) return person
  }
  return null
}

function timecode(value: number | null): string {
  if (value === null) return '—'
  const seconds = Math.max(0, Math.floor(value / 1000))
  const minutes = Math.floor(seconds / 60)
  return `${String(minutes).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`
}

function sourceTimecode(evidence: OperatorEvidenceDto): string | null {
  if (evidence.timecodeStartMs === null && evidence.timecodeEndMs === null) {
    return null
  }
  return `${timecode(evidence.timecodeStartMs)}–${timecode(evidence.timecodeEndMs)}`
}

function readable(value: string | null): string {
  return value
    ? (() => {
        const normalized = value.replace(/[_-]/g, ' ').toLowerCase()
        return normalized.charAt(0).toUpperCase() + normalized.slice(1)
      })()
    : 'Not recorded'
}

export function EvidenceDrawer({
  event,
  timeline,
  onClose,
}: EvidenceDrawerProps) {
  const [drawer, setDrawer] =
    useState<OperatorEventEvidenceDrawerDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const closeRef = useRef<HTMLButtonElement>(null)
  const dialogRef = useRef<HTMLElement>(null)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    let active = true
    setLoading(true)
    setError(null)
    operatorApi.getEventEvidence(event.id)
      .then(({ data }) => {
        if (active) setDrawer(data)
      })
      .catch(() => {
        if (active) {
          setDrawer(null)
          setError('Evidence could not be loaded. Restricted source content remains hidden.')
        }
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [event.id])

  useEffect(() => {
    const previouslyFocused = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null
    closeRef.current?.focus()
    const handleDialogKeys = (keyboardEvent: KeyboardEvent) => {
      if (keyboardEvent.key === 'Escape') {
        onCloseRef.current()
        return
      }
      if (keyboardEvent.key !== 'Tab') return
      const focusable = [...(dialogRef.current?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), select:not([disabled]), textarea:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ) ?? [])]
      if (focusable.length === 0) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const active = document.activeElement
      if (keyboardEvent.shiftKey && (active === first || !dialogRef.current?.contains(active))) {
        keyboardEvent.preventDefault()
        last.focus()
      } else if (!keyboardEvent.shiftKey && active === last) {
        keyboardEvent.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', handleDialogKeys)
    return () => {
      document.removeEventListener('keydown', handleDialogKeys)
      previouslyFocused?.focus()
    }
  }, [])

  const fullyAuthorized = drawer?.fullyAuthorized === true
    && drawer.withheldEvidenceCount === 0
  const accountable = fullyAuthorized
    ? participant(
        timeline,
        event.accountableOwnerId ?? event.accountableParticipantId,
      )
    : null
  const responsibles = fullyAuthorized
    ? event.responsibleParticipantIds
        .map((id) => participant(timeline, id))
        .filter((person): person is NonNullable<typeof person> =>
          person !== null)
    : []
  const participants = fullyAuthorized
    ? event.participantIds
        .map((id) => participant(timeline, id))
        .filter((person): person is NonNullable<typeof person> =>
          person !== null)
    : []
  const evidence = fullyAuthorized ? drawer.evidence : []
  const visibleEvidenceCount = drawer?.visibleEvidenceCount ?? evidence.length
  const withheldEvidenceCount = drawer?.withheldEvidenceCount ?? 0

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-background/55 backdrop-blur-[1px]"
      role="presentation"
      onMouseDown={(mouseEvent) => {
        if (mouseEvent.target === mouseEvent.currentTarget) onClose()
      }}
    >
      <aside
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`evidence-title-${event.id}`}
        className="h-full w-full overflow-y-auto border-l border-border bg-card text-foreground sm:max-w-[480px]"
      >
        <header className="sticky top-0 z-10 flex min-h-14 items-start gap-3 border-b border-border bg-card/95 px-4 py-3 backdrop-blur">
          <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md border border-primary/20 bg-primary/10 text-primary">
            <Fingerprint size={15} aria-hidden="true" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              Evidence ledger
            </p>
            <h2
              id={`evidence-title-${event.id}`}
              className="truncate text-sm font-semibold"
            >
              {fullyAuthorized
                ? event.title ?? readable(event.eventType)
                : 'Restricted event evidence'}
            </h2>
          </div>
          <button
            ref={closeRef}
            type="button"
            aria-label="Close evidence"
            onClick={onClose}
            className="flex size-8 items-center justify-center rounded-md text-muted-foreground outline-none hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-primary/50"
          >
            <X size={16} aria-hidden="true" />
          </button>
        </header>

        <div className="space-y-5 p-4">
          {fullyAuthorized && (
            <section aria-labelledby="extraction-heading" className="rounded-lg border border-border bg-background">
            <div className="border-b border-border px-3 py-2">
              <h3 id="extraction-heading" className="text-xs font-semibold">
                Extraction
              </h3>
            </div>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-3 p-3 text-xs">
              <div>
                <dt className="text-[10px] uppercase tracking-wide text-muted-foreground">Observed</dt>
                <dd className="mt-1">{readable(event.observedStatus)}</dd>
              </div>
              <div>
                <dt className="text-[10px] uppercase tracking-wide text-muted-foreground">Current status</dt>
                <dd className="mt-1">{readable(event.status)}</dd>
              </div>
              <div className="col-span-2">
                <dt className="text-[10px] uppercase tracking-wide text-muted-foreground">Extracted summary</dt>
                <dd className="mt-1 leading-5">
                  {loading
                    ? 'Loading authorized extraction…'
                    : drawer?.event.summary
                      ?? 'No authorized summary is available.'}
                </dd>
              </div>
            </dl>
            </section>
          )}

          {fullyAuthorized && (
            <section aria-labelledby="people-heading">
            <h3 id="people-heading" className="mb-2 flex items-center gap-2 text-xs font-semibold">
              <UsersRound size={13} className="text-muted-foreground" aria-hidden="true" />
              People and roles
            </h3>
            <div className="grid gap-2 sm:grid-cols-2">
              <RoleCard label="Accountable" names={accountable ? [accountable.name] : []} />
              <RoleCard label="Responsible" names={responsibles.map((person) => person.name)} />
            </div>
            <p className="mt-2 text-[11px] text-muted-foreground">
              Participants · {participants.map((person) => person.name).join(', ') || 'None recorded'}
            </p>
            </section>
          )}

          <section aria-labelledby="source-heading">
            <div className="mb-2 flex items-center justify-between gap-3">
              <h3 id="source-heading" className="text-xs font-semibold">
                Source records
              </h3>
              <span className="text-[10px] tabular-nums text-muted-foreground">
                {visibleEvidenceCount} visible / {withheldEvidenceCount} withheld
              </span>
            </div>
            {loading && (
              <div role="status" className="rounded-lg border border-border bg-background p-3 text-xs text-muted-foreground">
                Loading authorized evidence…
              </div>
            )}
            {error && <p role="alert" className="mb-2 text-xs text-destructive">{error}</p>}
            {!loading && (
              <div className="space-y-2">
                {evidence.map((item) => (
                  <article key={item.id} className="rounded-lg border border-border bg-background p-3">
                    <div className="flex flex-wrap items-center gap-2 text-[10px] text-muted-foreground">
                      <span className="font-semibold uppercase tracking-wide text-foreground">
                        {readable(item.sourceType)}
                      </span>
                      <span>·</span>
                      <time dateTime={item.occurredAt}>
                        {new Intl.DateTimeFormat('en-US', {
                          timeZone: 'UTC',
                          dateStyle: 'medium',
                          timeStyle: 'short',
                        }).format(new Date(item.occurredAt))}
                      </time>
                      {sourceTimecode(item) && (
                        <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-foreground">
                          {sourceTimecode(item)}
                        </span>
                      )}
                    </div>
                    <p className="mt-2 text-xs leading-5">
                      {item.safeExcerpt ?? 'Source unavailable under current permissions'}
                    </p>
                    <div className="mt-3 flex flex-wrap items-end justify-between gap-3">
                      <div className="flex items-start gap-1.5">
                        <ShieldCheck size={11} className="mt-0.5 text-primary" aria-hidden="true" />
                        <dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 text-[10px]">
                          <dt className="text-muted-foreground">Basis</dt>
                          <dd className="text-foreground">{readable(item.authorizationBasis)}</dd>
                          <dt className="text-muted-foreground">Account</dt>
                          <dd className="break-all text-foreground">{item.sourceAccountId}</dd>
                          <dt className="text-muted-foreground">Channel</dt>
                          <dd className="break-all text-foreground">
                            {item.sourceChannelId ?? 'Not recorded'}
                          </dd>
                          <dt className="text-muted-foreground">Observed people</dt>
                          <dd className="text-foreground">
                            {fullyAuthorized
                              ? item.observedParticipantIds
                                  .map((participantId) =>
                                    participant(timeline, participantId)?.name
                                      ?? participantId)
                                  .join(', ') || 'None recorded'
                              : `${item.observedParticipantIds.length} recorded`}
                          </dd>
                        </dl>
                      </div>
                      {item.deepLink && (
                        <a
                          href={item.deepLink}
                          className="inline-flex items-center gap-1 text-[10px] font-semibold text-primary outline-none hover:underline focus-visible:ring-2 focus-visible:ring-primary/50"
                        >
                          Open source
                          <ArrowUpRight size={11} aria-hidden="true" />
                        </a>
                      )}
                    </div>
                  </article>
                ))}
                {withheldEvidenceCount > 0 && (
                  <article
                    className="rounded-lg border border-dashed border-border bg-muted/20 p-3"
                  >
                    <p className="flex items-center gap-2 text-xs text-muted-foreground">
                      <FileLock2 size={13} aria-hidden="true" />
                      Source unavailable under current permissions
                    </p>
                  </article>
                )}
              </div>
            )}
          </section>

          {fullyAuthorized && (
            <section aria-labelledby="reasoning-heading">
            <h3 id="reasoning-heading" className="mb-2 text-xs font-semibold">
              Persisted extraction provenance
            </h3>
            <div className="space-y-2">
              {!loading && (drawer?.inferences.length ?? 0) === 0 && (
                <p className="rounded-lg border border-dashed border-border bg-muted/20 p-3 text-xs text-muted-foreground">
                  No authorized inference provenance is linked to this event.
                </p>
              )}
              {drawer?.inferences.map((inference) => (
                <article
                  key={inference.id}
                  className="rounded-lg border border-border bg-background p-3"
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-foreground">
                      {readable(inference.inferenceType)}
                    </span>
                    <span className="text-[10px] font-medium text-primary">
                      {Math.round(inference.confidence * 100)}% confidence
                    </span>
                  </div>
                  <p className="mt-2 text-xs leading-5">{inference.reason}</p>
                  <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-2 gap-y-1 text-[10px]">
                    <dt className="text-muted-foreground">Model</dt>
                    <dd>{inference.providerIdentifier} · {inference.modelIdentifier}</dd>
                    <dt className="text-muted-foreground">Prompt</dt>
                    <dd>{inference.promptKey} · revision {inference.promptRevision}</dd>
                    <dt className="text-muted-foreground">Extraction</dt>
                    <dd>{inference.extractionVersion}</dd>
                    <dt className="text-muted-foreground">Tasks</dt>
                    <dd>
                      {inference.linkedTasks
                        .map(({ deliverable }) => deliverable)
                        .join(', ') || 'None'}
                    </dd>
                    <dt className="text-muted-foreground">Milestones</dt>
                    <dd>
                      {inference.linkedMilestones
                        .map(({ title }) => title)
                        .join(', ') || 'None'}
                    </dd>
                  </dl>
                  <div className="mt-3 border-t border-border pt-2">
                    <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                      <Fingerprint size={11} aria-hidden="true" />
                      Ordered audit
                    </p>
                    <ol className="mt-1 space-y-1 text-[10px] text-muted-foreground">
                      {inference.audit.map((audit) => (
                        <li key={audit.id}>
                          {readable(audit.eventType)} ·{' '}
                          <time dateTime={audit.occurredAt}>
                            {new Date(audit.occurredAt).toISOString()}
                          </time>
                        </li>
                      ))}
                      {inference.audit.length === 0 && <li>No audit rows</li>}
                    </ol>
                  </div>
                </article>
              ))}
            </div>
            </section>
          )}
        </div>
      </aside>
    </div>
  )
}

function RoleCard({ label, names }: { label: string; names: string[] }) {
  return (
    <div className="rounded-lg border border-border bg-background p-3">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className="mt-1 text-xs">{names.join(', ') || 'Unassigned'}</p>
    </div>
  )
}
